import React, { useState, useEffect } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';

import { useDispatch, useSelector } from "react-redux";
import { createPreorderEvent, refreshPreorderEventsView } from '../../../actions/preorderEventActions/PreorderEventActions';
import { RootStore } from "../../../Store";

import { useTranslation } from 'react-i18next';

import type { CreatePreorderEventRequest } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

import useStyles from './AddPreorderEventModalStyles';

interface AddPreorderEventModalProps {
  open: boolean;
  onClose: () => void;
}

const AddPreorderEventModal: React.FC<AddPreorderEventModalProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const [t] = useTranslation('global');
  const dispatch = useDispatch();

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { loading, actionStatus } = preorderEventState;

  const [formData, setFormData] = useState<CreatePreorderEventRequest>({
    name: '',
    releaseDate: '',
    lastPreorderDate: '',
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Handle successful creation
  useEffect(() => {
    if (actionStatus?.status === 'success' && open) {
      onClose();
      setFormData({
        name: '',
        releaseDate: '',
        lastPreorderDate: '',
      });
      setErrors({});
      dispatch(refreshPreorderEventsView(true));
    }
  }, [actionStatus, open, onClose, dispatch]);

  const handleClose = () => {
    setFormData({
      name: '',
      releaseDate: '',
      lastPreorderDate: '',
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (field: keyof CreatePreorderEventRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const getDateErrorMessage = (): string => {
    if (!formData.releaseDate || !formData.lastPreorderDate) {
      return t('common.field-required');
    }
    
    const currentDate = new Date();
    const releaseDate = new Date(formData.releaseDate);
    const lastPreorderDate = new Date(formData.lastPreorderDate);
    
    // Check if dates are in the past
    if (releaseDate <= currentDate || lastPreorderDate <= currentDate) {
      return t('preorderEvents.invalid-dates-error');
    }
    
    // Check if release date is not after last preorder date
    if (lastPreorderDate >= releaseDate) {
      return t('preorderEvents.last-preorder-before-release');
    }
    
    return t('common.field-required');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};

    if (!formData.name.trim()) {
      newErrors.name = true;
    }

    if (!formData.releaseDate) {
      newErrors.releaseDate = true;
    }

    if (!formData.lastPreorderDate) {
      newErrors.lastPreorderDate = true;
    }

    // Validate dates logic
    if (formData.releaseDate && formData.lastPreorderDate) {
      const currentDate = new Date();
      const releaseDate = new Date(formData.releaseDate);
      const lastPreorderDate = new Date(formData.lastPreorderDate);
      
      // Check if both dates are in the future
      if (releaseDate <= currentDate || lastPreorderDate <= currentDate) {
        newErrors.lastPreorderDate = true;
      }
      
      // Check if release date is after last preorder date  
      if (lastPreorderDate >= releaseDate) {
        newErrors.lastPreorderDate = true;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(createPreorderEvent(formData));
    }
  };

  const isLoading = loading === 'CREATE_PREORDER_EVENT';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        {t('preorderEvents.add-preorder-event')}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('preorderEvents.name')}
              value={formData.name}
              onChange={handleInputChange('name')}
              error={errors.name}
              helperText={errors.name ? t('common.field-required') : ''}
              variant="outlined"
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('preorderEvents.release-date')}
              type="date"
              value={formData.releaseDate}
              onChange={handleInputChange('releaseDate')}
              error={errors.releaseDate}
              helperText={errors.releaseDate ? t('common.field-required') : ''}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                placeholder: 'yyyy-mm-dd'
              }}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('preorderEvents.last-preorder-date')}
              type="date"
              value={formData.lastPreorderDate}
              onChange={handleInputChange('lastPreorderDate')}
              error={errors.lastPreorderDate}
              helperText={errors.lastPreorderDate ? getDateErrorMessage() : ''}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                placeholder: 'yyyy-mm-dd'
              }}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} color="default">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isLoading}
        >
          {isLoading ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPreorderEventModal;